import {Injectable} from '@angular/core';
import {BehaviorSubject, filter, firstValueFrom, Observable, take, tap} from "rxjs";
import {ProductCatalogue} from "../dto/product-catalogue";
import {ProductPackage} from "../dto/product-package";
import {HttpClient} from "@angular/common/http";
import {environment} from "../../../environments/environment";
import {IframeConfiguration} from "../dto/configuration/IframeConfiguration";
import {ParentWindowService} from "./parent-window.service";

@Injectable({
  providedIn: 'root'
})
export class ConfigurationService {
  private readonly dataServiceUrl: string = environment.dataServiceUrl;
  private readonly defaultConfigurationId: string = environment.defaultConfigurationId;

  // Subject + Observable for the ProductCatalogue
  private productCatalogueSubject = new BehaviorSubject<ProductCatalogue | undefined>(undefined);
  productCatalogue$ = this.productCatalogueSubject.asObservable();

  // Subject + Observable for the selected ProductPackage
  private selectedProductPackageSubject = new BehaviorSubject<ProductPackage | undefined>(undefined);
  selectedProductPackage$ = this.selectedProductPackageSubject.asObservable();

  private configurationSubject = new BehaviorSubject<IframeConfiguration | undefined>(undefined);
  configurationObservable = this.configurationSubject.asObservable();

  constructor(private http: HttpClient, private parentWindowService: ParentWindowService) {
    const sessionConfigurationId = sessionStorage.getItem('IframeConfigId');
    const configurationId = sessionConfigurationId ? sessionConfigurationId : this.getConfigurationId()

    if (configurationId) {
      sessionStorage.setItem('IframeConfigId', configurationId);
      this.fetchConfiguration(configurationId).subscribe({
        next: async (configuration) => {
          if (configuration) {
            await this.retrieveOrigin();
            console.log(`Fetched Configuration with ProductCatalogue ${configuration.productCatalogue.name}`);
          }
        },
        error: () => {
          console.warn('Could not fetch requested ProductCatalogue!');
        }
      });
    } else {
      console.warn('No catalogueId present => Using default');
      sessionStorage.setItem('IframeConfigId', this.defaultConfigurationId);
      this.fetchConfiguration(this.defaultConfigurationId).subscribe({
        next: (res) => {
          if (res) {
            sessionStorage.setItem('origin', 'SYSTEM');
            console.log(`Fetched default Configuration with ProductCatalogue ${res.productCatalogue.name}`);
          }
        },
        error: () => {
          console.warn('Could not fetch default ProductCatalogue!');
        }
      });
    }
  }

  /**
   * Retrieves the IframeConfiguration for a specific configurationId.
   * Sorts the product packages inside the configuration.
   *
   * @param configurationId Id of the IframeConfiguration that should be retrieved
   * @return Observable emitting the retrieved IframeConfiguration
   */
  private fetchConfiguration(configurationId: string): Observable<IframeConfiguration>{
    const url = `${this.dataServiceUrl}/anonymous/iframe-configurations/${configurationId}`;
    return this.http.get<IframeConfiguration>(url).pipe(
      tap(configuration => {
        // Sort the product packages by price
        configuration.productCatalogue.productPackages.sort( (a,b) => (a.price < b.price ? -1 : 1));
        // Sort product within each product package by their name
        configuration.productCatalogue.productPackages.forEach(productPackage => {
          productPackage.products.sort((a,b) => a.nameShort.localeCompare(b.nameShort));
        });
        this.preselectPackage(configuration.productCatalogue);
        this.configurationSubject.next(configuration);
        this.productCatalogueSubject.next(configuration.productCatalogue);
      })
    );
  }

  /**
   * Try to get the domain via PostMessageAPI
   */
  async retrieveOrigin(): Promise<string> {
    console.log('Retrieving domain...');

    // Attempt to get the origin URL from the parent window
    const origin = await firstValueFrom(this.parentWindowService.requestParentWindowUrl()).catch((err) => {
      console.warn(err);
      return 'SYSTEM';
    });

    // Store the origin in session storage if verification succeeds
    sessionStorage.setItem('origin', origin);
    return origin;
  }

  /**
   * Retrieves the IframeConfigId from the request parameters of the URL
   *
   * @return IframeConfigId if found else null
   */
  private getConfigurationId(): string | null{
    const urlParams = new URLSearchParams(window.location.search);
    return urlParams.get('IframeConfigId');
  }

  /**
   * Waits for the selected product package to be available and returns it as a Promise
   *
   * @return Promise that resolves to the selected ProductPackage.
   */
  public  waitForProductPackage(): Promise<ProductPackage> {
    return firstValueFrom(
      this.selectedProductPackage$.pipe(
        filter((productPackage): productPackage is ProductPackage => productPackage !== undefined),
        take(1)
      )
    );
  }

  /**
   * Waits for the IframeConfiguration to be available and returns it as a Promise
   *
   * @return Promise that resolves to the current IframeConfiguration
   */
  public waitForConfiguration(): Promise<IframeConfiguration>{
    return firstValueFrom(
      this.configurationObservable.pipe(
        filter((config): config is IframeConfiguration => config !== undefined),
        take(1)
      )
    );
  }

  /**
   * Fetches the description of a product package.
   * The fetched description is a plain html text.
   *
   * @param productPackageUuid UUID of the product package for which the description should be retrieved
   * @return Observable containing the product package description as plain text.
   */
  public fetchProductPackageDescription(productPackageUuid: string){
    const url = `https://docestate-cdn.s3.eu-central-1.amazonaws.com/product-packages/${productPackageUuid}.html`
    return this.http.get(url, {responseType: 'text'});
  }

  /**
   * Retrieves the current product catalogue from the productCatalogueSubject
   *
   * @return The current ProductCatalogue if present else undefined
   */
  public getProductCatalogue(): ProductCatalogue | undefined {
    return this.productCatalogueSubject.value;
  }

  /**
   * Sets the selected product package in the productCatalogueSubject
   *
   * @param productPackage ProductPackage to set as selected
   */
  public setSelectedProductPackage(productPackage: ProductPackage): void {
    this.selectedProductPackageSubject.next(productPackage);
  }

  /**
   * Sets the selected product package in the productCatalogueSubject to undefined
   */
  public resetSelectedProductPackage(): void {
    this.selectedProductPackageSubject.next(undefined);
  }

  /**
   * Retrieves the currently selected product package.
   *
   * @return Current selected product package if present else undefined
   */
  public getSelectedProductPackage(): ProductPackage | undefined {
    return this.selectedProductPackageSubject.value;
  }

  /**
   * Preselects a product package from the provided product catalogue based on stored session data
   *
   * @param catalogue ProductCatalogue from which the product package should be pre selected
   */
  private preselectPackage(catalogue: ProductCatalogue): void {
    const packageIndexItem = sessionStorage.getItem('packageIndex');
    let packageIndex : number | undefined = undefined;

    if (packageIndexItem) {
      packageIndex = JSON.parse(packageIndexItem) as number;
    }

    if (packageIndex !== undefined) {
      if (packageIndex < catalogue.productPackages.length) {
        this.selectedProductPackageSubject.next(catalogue.productPackages[packageIndex]);
      } else {
        console.error(`PackageIndex out of bounds for index ${packageIndex} and catalogue ${catalogue.name} with ${catalogue.productPackages.length} packages!`);
      }
    }
  }

}
